
import { createContext, useContext, onMount } from "solid-js";
import { createStore, SetStoreFunction } from "solid-js/store";
import socket from "./socket";

const defaultUserStore = {
  isAuthenticated: false,
  isConnected: false,
  isAuthenticating: false,
  username: null as string | null,
  role: null as string | null,
  userType: null as 'cas-user' | 'external-student' | 'anonymous-student' | null,
  clientId: null as string | null,
  authStage: 'choose-method' as 'choose-method' | 'enter-external-name' | 'authenticating' | 'authenticated',
};

type UserActions = {
  updateStudentName: (name: string) => void;
  chooseCAS: () => void;
  chooseExternal: () => void;
  chooseAnonymous: () => void;
  submitExternalName: (name: string) => void;
  submitAnonymousLogin: () => void;
};

const UserContext = createContext<
  [typeof defaultUserStore, SetStoreFunction<typeof defaultUserStore>, UserActions]
>();


// Generate a new client ID for a localStorage key (do not create automatically at startup)
function generateClientId(storageKey: 'clientId' | 'anonymousClientId' = 'clientId'): string {
  const clientId = 'client_' + crypto.randomUUID().replaceAll('-', '');
  localStorage.setItem(storageKey, clientId);
  return clientId;
}

// retrieve query params 'jwt'
const urlParams = new URLSearchParams(window.location.search);
const jwt = urlParams.get('jwt') || localStorage.getItem('jwt');
if(jwt) {
  localStorage.setItem('jwt', jwt);
}
if(urlParams.get('jwt')) {
  // Remove jwt from URL and restore page where CAS login was initiated.
  const postLoginRedirect = localStorage.getItem('postLoginRedirect');
  if (postLoginRedirect) {
    localStorage.removeItem('postLoginRedirect');
    window.history.replaceState({}, document.title, postLoginRedirect);
  } else {
    window.history.replaceState({}, document.title, "/");
  }
}

export function UserContextProvider(props: any) {
  const [userStore, setUserStore] = createStore(defaultUserStore);

  
  onMount(() => {
    console.log('Mounted UserContext');

    socket.on("connect", () => {

      setUserStore({ isConnected: true });
      console.log("connected");

      if(jwt) {
        // Authenticate via CAS token
        setUserStore({ authStage: 'authenticating', isAuthenticating: true });
        socket.emit("authenticate", jwt);
      } else {
        const isDirectQuizJoinPage = window.location.pathname.startsWith('/q/');
        const existingClientId = localStorage.getItem('clientId');
        const storedName = localStorage.getItem('studentName');
        if (userStore.isAuthenticated && userStore.userType === 'external-student' && existingClientId && storedName) {
          socket.emit('authenticate-student', {
            clientId: existingClientId,
            name: storedName,
            studentToken: localStorage.getItem('studentToken')
          });
        } else if (userStore.isAuthenticated && userStore.userType === 'anonymous-student') {
          socket.emit('authenticate-student', {
            clientId: localStorage.getItem('anonymousClientId'),
            anonymous: true,
            studentToken: localStorage.getItem('anonymousStudentToken')
          });
        } else if(existingClientId && storedName && !isDirectQuizJoinPage) {
          // Auto external authentication (returning student)
          setUserStore({ 
            clientId: existingClientId, 
            authStage: 'authenticating', 
            isAuthenticating: true 
          });
          socket.emit('authenticate-student', {
            clientId: existingClientId,
            name: storedName,
            studentToken: localStorage.getItem('studentToken')
          });
        } else {
          // Let the user choose between CAS and External
          setUserStore({ 
            clientId: existingClientId, 
            authStage: 'choose-method' 
          });
        }
      }
    });

    socket.on("authenticated", (data: { username: string; fullName?: string | null; role: string; type: 'cas-user' | 'external-student' | 'anonymous-student'; clientId?: string; studentToken?: string }) => {
        console.log('connected as', data);

        setUserStore({
          username: (data.type === 'external-student' || data.type === 'anonymous-student') ? (data.fullName || '') : data.username,
          role: data.role, 
          isAuthenticating: false,
          isAuthenticated: true,
          userType: data.type,
          clientId: data.clientId || userStore.clientId,
          authStage: 'authenticated'
        });
        if (data.type === 'external-student' && data.fullName) {
          // Persist name after successful auth
          localStorage.setItem('studentName', data.fullName);
        }
        if (data.studentToken) {
          localStorage.setItem(
            data.type === 'anonymous-student' ? 'anonymousStudentToken' : 'studentToken',
            data.studentToken
          );
        }
    });

    socket.on('student-reauth-required', (data: { authType: 'external' | 'anonymous' }) => {
      const anonymous = data.authType === 'anonymous';
      localStorage.removeItem(anonymous ? 'anonymousClientId' : 'clientId');
      localStorage.removeItem(anonymous ? 'anonymousStudentToken' : 'studentToken');
      setUserStore({
        isAuthenticated: false,
        isAuthenticating: false,
        username: null,
        role: null,
        userType: null,
        clientId: null,
        authStage: anonymous ? 'choose-method' : 'enter-external-name'
      });
    });

    socket.on("student-updated", (data: { username?: string; fullName?: string; clientId: string }) => {
        console.log('student updated', data);
        const display = data.fullName || data.username || '';
        setUserStore({ username: display });
        localStorage.setItem('studentName', display);
    });

    socket.on("unauthorized", (error: any) => {
        console.log({error});
        localStorage.removeItem('jwt');
        setUserStore({
          isAuthenticated: false,
          username: null,
          role: null,
          isAuthenticating: false,
          userType: null,
          authStage: 'choose-method',
        });
    });

  });

  const actions: UserActions = {
    updateStudentName: (name: string) => {
      if (userStore.clientId && userStore.userType === 'external-student') {
        socket.emit('update-student-name', { name });
      }
    },
    chooseCAS: () => {
      localStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search);
      window.location.href = '/login';
    },
    chooseExternal: () => {
      // Move to name entry stage; pre-fill clientId if exists
      const existingClientId = localStorage.getItem('clientId');
      setUserStore({
        isAuthenticated: false,
        isAuthenticating: false,
        username: null,
        role: null,
        userType: null,
        authStage: 'enter-external-name',
        clientId: existingClientId || null
      });
    },
    chooseAnonymous: () => {
      actions.submitAnonymousLogin();
    },
    submitExternalName: (name: string) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      // Ensure clientId exists
      let clientId = userStore.clientId;
      if (!clientId) {
        clientId = generateClientId();
        setUserStore({ clientId });
      }
      setUserStore({
        isAuthenticated: false,
        username: null,
        role: null,
        userType: null,
        authStage: 'authenticating',
        isAuthenticating: true
      });
      socket.emit('authenticate-student', {
        clientId,
        name: trimmed,
        studentToken: localStorage.getItem('studentToken')
      });
    },
    submitAnonymousLogin: () => {
      let clientId = localStorage.getItem('anonymousClientId');
      if (!clientId) {
        clientId = generateClientId('anonymousClientId');
      }

      setUserStore({
        isAuthenticated: false,
        username: null,
        role: null,
        userType: null,
        clientId,
        authStage: 'authenticating',
        isAuthenticating: true
      });
      socket.emit('authenticate-student', {
        clientId,
        anonymous: true,
        studentToken: localStorage.getItem('anonymousStudentToken')
      });
    }
  }

  return (
    <UserContext.Provider value={[userStore, setUserStore, actions]}>
      {props.children}
    </UserContext.Provider>
  );
}


export function useUserContext() { return useContext(UserContext); }
